import { useCallback } from "react";
import { useLegislationTableStore } from "@/stores/legislation-table-state.store";
import {
  isScopedToDatasource,
  withDatasourceFilter,
} from "@/pages/legislation/utils/datasourceFilter";

/**
 * Wires the datasource breadcrumb of a legislation viewer, so that returning to
 * Kilder through it lands on the list the user left: scoped to this source, with
 * the rest of their filters, their page and their sort order intact.
 *
 * Returns the `getDatasourceFrameOnClick` the breadcrumbs ask for — a handler
 * for the frame of the source being viewed, and nothing for any other frame.
 */
export const useDatasourceBreadcrumbReturn = (
  datasource: string | undefined
): ((datasourceId: string) => (() => void) | undefined) => {
  const { columnFilters, setTableState, setNavigateBack } =
    useLegislationTableStore();

  const returnToList = useCallback(
    (datasourceId: string) => {
      // Coming back to a list the user already had scoped to this source is a
      // return, not a new source selection: say so, or the table drops the page
      // and the sort order they left it on.
      if (isScopedToDatasource(columnFilters, datasourceId)) {
        setNavigateBack(true);
      }

      setTableState({
        columnFilters: withDatasourceFilter(columnFilters, datasourceId),
      });
    },
    [columnFilters, setNavigateBack, setTableState]
  );

  return useCallback(
    (datasourceId: string) => {
      if (!datasource || datasource.toLowerCase() !== datasourceId) {
        return undefined;
      }
      return () => returnToList(datasourceId);
    },
    [datasource, returnToList]
  );
};
